/**
 * Composant d'affichage des variantes de produit commandées
 * Affiche les options sélectionnées, quantités et prix
 * Créé le 23 juillet 2026
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Palette as PaletteIcon,
  Straighten as StraightenIcon,
  Category as CategoryIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../constants/dashboardConstants';

const ProductVariantDisplay = ({ variantSelections, currency = 'EUR' }) => {
  const { t } = useTranslation(['common', 'delivery']);

  // Si pas de variantes, ne rien afficher
  if (!variantSelections || variantSelections.length === 0) {
    return null;
  }

  // Parse si c'est une string JSON
  const variants = typeof variantSelections === 'string' 
    ? JSON.parse(variantSelections) 
    : variantSelections;

  if (!Array.isArray(variants) || variants.length === 0) {
    return null;
  }

  // Fonction pour obtenir l'icône selon le type de variante
  const getVariantIcon = (type) => {
    const iconMap = {
      'color': <PaletteIcon sx={{ fontSize: 18 }} />,
      'couleur': <PaletteIcon sx={{ fontSize: 18 }} />,
      'size': <StraightenIcon sx={{ fontSize: 18 }} />,
      'taille': <StraightenIcon sx={{ fontSize: 18 }} />,
      'category': <CategoryIcon sx={{ fontSize: 18 }} />,
      'categorie': <CategoryIcon sx={{ fontSize: 18 }} />
    };
    return iconMap[type?.toLowerCase()] || <LocalOfferIcon sx={{ fontSize: 18 }} />;
  };

  // Fonction pour formater le label du type
  const formatTypeLabel = (type) => {
    const typeLabels = {
      'color': t('color', 'Couleur'),
      'couleur': t('color', 'Couleur'),
      'size': t('size', 'Taille'),
      'taille': t('size', 'Taille'),
      'category': t('category', 'Catégorie'),
      'categorie': t('category', 'Catégorie')
    };
    return typeLabels[type?.toLowerCase()] || type || t('option', 'Option');
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography 
        variant="subtitle1" 
        gutterBottom 
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
      >
        <ShoppingCartIcon color="primary" />
        {t('productDetails', 'Détails du produit')}
      </Typography>

      <Paper 
        elevation={0} 
        sx={{ 
          p: 2.5, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e8f4f8 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: 2
        }}
      >
        <Grid container spacing={2}>
          {variants.map((variant, index) => (
            <Grid item xs={12} key={index}>
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'white',
                  borderLeft: '4px solid',
                  borderLeftColor: 'primary.main',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Type et valeur */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {getVariantIcon(variant.type)}
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}
                      >
                        {formatTypeLabel(variant.type)}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {variant.value}
                    </Typography>
                    {variant.sku && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          color: 'text.secondary',
                          backgroundColor: 'grey.100',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                          mt: 0.5
                        }}
                      >
                        SKU: {variant.sku}
                      </Typography>
                    )}
                  </Grid>

                  {/* Quantité */}
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('quantity', 'Quantité')}
                    </Typography>
                    <Chip 
                      label={`× ${variant.quantity || 1}`}
                      size="medium"
                      color="primary"
                      sx={{ fontWeight: 700, fontSize: '1rem' }}
                    />
                  </Grid>

                  {/* Prix unitaire */}
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('unitPrice', 'Prix unitaire')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(variant.price || 0, currency)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Prix total si quantité > 1 */}
                {variant.quantity > 1 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('subtotal', 'Sous-total')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        {formatCurrency((variant.price || 0) * (variant.quantity || 1), currency)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Total général si plusieurs variantes */}
        {variants.length > 1 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                backgroundColor: 'primary.main',
                color: 'white',
                p: 2,
                borderRadius: 1.5,
                mt: 2
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t('total', 'TOTAL')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {formatCurrency(
                  variants.reduce((total, v) => total + ((v.price || 0) * (v.quantity || 1)), 0),
                  currency
                )}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProductVariantDisplay;
